/* eslint-disable react/jsx-no-constructed-context-values */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMutation, useQuery, useSubscription } from "@apollo/client";
import { enqueueSnackbar } from "notistack";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";
import { BROWSER_CALL_AGENT_SIGNALING } from "src/graphql/subscriptions/browserCall/browserCallSubscriptions";
import {
  REJECT_BROWSER_CALL,
  END_BROWSER_CALL,
  UPLOAD_BROWSER_CALL_RECORDING,
} from "src/graphql/mutation/browserCall/browserCallMutations";
import {
  GET_ACTIVE_BROWSER_CALL,
  GET_PENDING_INCOMING_BROWSER_CALLS,
} from "src/graphql/query/browserCall/browserCallQueries";

// Lightweight summary of an incoming browser call shown in the inbox-side
// toast. The pop-out window owns the real peer connection.
export interface IncomingBrowserCall {
  roomId: string;
  customerName: string | null;
  customerIdentifier: string | null;
  channel: string;
  mediaMode: string;
  sdpOffer: string;
  receivedAt: number;
}

interface BrowserCallAgentContextValue {
  incomingQueue: IncomingBrowserCall[];
  activeRoomId: string | null;
  acceptCall: (roomId: string) => void;
  rejectCall: (roomId: string) => Promise<void>;
  endActiveCall: () => Promise<void>;
  focusPopout: () => void;
}

const Ctx = createContext<BrowserCallAgentContextValue | null>(null);

// Channel used to sync pop-out <-> main tab. Picked up by AgentCallPopout
// and reciprocally listened to here.
const CHANNEL_NAME = "browser_call_channel";
const SESSION_KEY = "browserCallSession";

const POPOUT_FEATURES = "width=420,height=640,resizable=yes,scrollbars=no";

// Map guest-reported issue codes to the snackbar text the agent should
// see. Mirrors the categories in GuestCallRoom.mapGuestError so the
// agent's wording matches what the customer is being shown.
function guestIssueSnackbarText(
  code: string | null | undefined,
  customerName: string | null | undefined,
): string {
  const who = customerName || "the customer";
  switch (code) {
    case "no_microphone":
      return `${who} couldn't join the call — no microphone detected on their device. Consider texting them.`;
    case "permission_blocked":
      return `${who} couldn't join — microphone access is blocked in their browser.`;
    case "mic_in_use":
      return `${who} couldn't join — their microphone is in use by another app.`;
    case "mic_unsupported":
      return `${who} couldn't join — their microphone isn't supported.`;
    case "insecure_context":
      return `${who} couldn't join — their browser flagged an insecure connection (HTTPS issue).`;
    case "generic_error":
    default:
      return `${who} couldn't join the call due to a setup error. Consider texting them.`;
  }
}

interface ProviderProps {
  children: React.ReactNode;
}

export const BrowserCallAgentProvider: React.FC<ProviderProps> = ({
  children,
}) => {
  const { agentId } = useActiveUserDetail();
  const [incomingQueue, setIncomingQueue] = useState<IncomingBrowserCall[]>([]);
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);

  const popoutRef = useRef<Window | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);

  const [rejectBrowserCall] = useMutation(REJECT_BROWSER_CALL);
  const [endBrowserCall] = useMutation(END_BROWSER_CALL);
  const [uploadBrowserCallRecording] = useMutation(
    UPLOAD_BROWSER_CALL_RECORDING,
  );

  // ── Fallback recording upload state ───────────────────────────────────────
  // The pop-out mirrors every MediaRecorder chunk to us via BroadcastChannel
  // so we can run a backup upload if the pop-out dies before its own upload
  // completes (window closed, browser crash, etc). Keyed by roomId.
  const recordingBuffersRef = useRef<
    Map<string, { chunks: Blob[]; mimeType: string }>
  >(new Map());
  // Per-room watchdog timers — started when the pop-out signals call-end,
  // cancelled when we receive RECORDING_UPLOADED. If they fire, we upload.
  const recordingWatchdogsRef = useRef<
    Map<string, ReturnType<typeof setTimeout>>
  >(new Map());
  // Rooms whose pop-out broadcast POPOUT_ENDED at the start of its own
  // endCall — i.e. the pop-out is alive and handling teardown. Lets us
  // distinguish "give the pop-out 60s to upload/retry" from "pop-out is
  // dead, upload now". Refresh / crash don't send POPOUT_ENDED.
  const popoutHandlingEndRef = useRef<Set<string>>(new Set());

  const cancelRecordingWatchdog = useCallback((roomId: string) => {
    const t = recordingWatchdogsRef.current.get(roomId);
    if (t) {
      clearTimeout(t);
      recordingWatchdogsRef.current.delete(roomId);
    }
  }, []);

  const runFallbackUpload = useCallback(
    async (roomId: string) => {
      const buf = recordingBuffersRef.current.get(roomId);
      if (!buf || !buf.chunks.length) return;
      const mime = buf.mimeType || "audio/webm";
      const blob = new Blob(buf.chunks, { type: mime });
      // Mirror the pop-out's threshold so we don't push 0.5KB blobs to S3.
      if (blob.size < 1024) {
        recordingBuffersRef.current.delete(roomId);
        return;
      }
      const file = new File([blob], `browser-call-${roomId}.webm`, {
        type: mime,
      });
      try {
        await uploadBrowserCallRecording({
          variables: { roomId, file },
          context: { hasUpload: true },
        });
      } catch (err) {
        // If the fallback also fails the recording-cleanup cron will sweep
        // the row to "Recording unavailable" after 5 min. Nothing more to do.
        // eslint-disable-next-line no-console
        console.warn("Main-tab fallback upload failed", err);
      } finally {
        recordingBuffersRef.current.delete(roomId);
      }
    },
    [uploadBrowserCallRecording],
  );

  const armRecordingWatchdog = useCallback(
    (roomId: string) => {
      if (!roomId) return;
      if (!recordingBuffersRef.current.has(roomId)) return; // nothing to upload
      if (recordingWatchdogsRef.current.has(roomId)) return; // already armed
      // Give the pop-out 60s to land its primary upload (covers a retry).
      const timeoutId = setTimeout(() => {
        recordingWatchdogsRef.current.delete(roomId);
        runFallbackUpload(roomId);
      }, 60_000);
      recordingWatchdogsRef.current.set(roomId, timeoutId);
    },
    [runFallbackUpload],
  );

  // After a page refresh, the React-memory `activeRoomId` is gone but the
  // pop-out + the backend row are still alive. Re-hydrate the banner state
  // by asking the server if this agent has any active/ringing call.
  const { data: activeCallData } = useQuery(GET_ACTIVE_BROWSER_CALL, {
    fetchPolicy: "network-only",
    skip: !agentId,
  });

  useEffect(() => {
    const existing = activeCallData?.getActiveBrowserCall;
    if (existing?.roomId && existing.status === "active") {
      setActiveRoomId((prev) => prev || existing.roomId);
    }
  }, [activeCallData]);

  // Re-hydrate the incoming-call toast + ringtone after a refresh by asking
  // the server which calls are currently ringing for this agent. The
  // subscription itself can't replay past `guest_joined` events.
  const { data: pendingIncomingData } = useQuery(
    GET_PENDING_INCOMING_BROWSER_CALLS,
    {
      fetchPolicy: "network-only",
      skip: !agentId,
    },
  );

  useEffect(() => {
    const list = pendingIncomingData?.getPendingIncomingBrowserCalls;
    if (!Array.isArray(list) || list.length === 0) return;
    setIncomingQueue((q) => {
      const existingIds = new Set(q.map((c) => c.roomId));
      const additions: IncomingBrowserCall[] = list
        .filter((c: any) => c?.roomId && !existingIds.has(c.roomId))
        .map((c: any) => ({
          roomId: c.roomId,
          customerName: c.customerName || null,
          customerIdentifier: c.customerIdentifier || null,
          channel: c.channel || "standalone",
          mediaMode: c.mediaMode || "audio",
          sdpOffer: c.sdpOffer,
          receivedAt: Date.now(),
        }));
      if (additions.length === 0) return q;
      return [...q, ...additions];
    });
  }, [pendingIncomingData]);

  // Set up the BroadcastChannel once.
  useEffect(() => {
    if (typeof window === "undefined" || !("BroadcastChannel" in window)) {
      return undefined;
    }
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = channel;
    channel.onmessage = (ev) => {
      const msg = ev.data || {};
      if (msg.type === "POPOUT_READY") {
        // Pop-out is alive; nothing to do — sessionStorage already has data.
      } else if (msg.type === "POPOUT_ACCEPTED") {
        setActiveRoomId(msg.roomId || null);
      } else if (msg.type === "POPOUT_ENDED") {
        setActiveRoomId(null);
        setIncomingQueue((q) => q.filter((c) => c.roomId !== msg.roomId));
        // Pop-out reached end-of-call. Mark it so the subscription's
        // "ended" event (arriving moments later) doesn't trigger the
        // immediate-fallback path. Then arm the watchdog as backup if
        // the pop-out's own upload never broadcasts UPLOADED.
        if (msg.roomId) {
          popoutHandlingEndRef.current.add(msg.roomId);
          armRecordingWatchdog(msg.roomId);
        }
      } else if (msg.type === "RECORDING_CHUNK") {
        // Pop-out mirroring a MediaRecorder chunk for backup upload.
        if (!msg.roomId || !msg.blob) return;
        const existing: { chunks: Blob[]; mimeType: string } =
          recordingBuffersRef.current.get(msg.roomId) || {
            chunks: [],
            mimeType: msg.mimeType || "audio/webm",
          };
        existing.chunks.push(msg.blob);
        if (msg.mimeType) existing.mimeType = msg.mimeType;
        recordingBuffersRef.current.set(msg.roomId, existing);
      } else if (msg.type === "RECORDING_UPLOADED") {
        // Pop-out's primary upload succeeded — drop our mirror buffer,
        // cancel the watchdog, and clear the alive-marker.
        if (msg.roomId) {
          recordingBuffersRef.current.delete(msg.roomId);
          cancelRecordingWatchdog(msg.roomId);
          popoutHandlingEndRef.current.delete(msg.roomId);
        }
      }
    };
    return () => {
      // Cancel all pending watchdogs on unmount so they don't fire after
      // we're gone (state would update via Apollo's noop, but no point).
      recordingWatchdogsRef.current.forEach((t) => clearTimeout(t));
      recordingWatchdogsRef.current.clear();
      channel.close();
      channelRef.current = null;
    };
  }, [armRecordingWatchdog, cancelRecordingWatchdog]);

  // Listen for incoming-call signaling on the agent channel.
  useSubscription(BROWSER_CALL_AGENT_SIGNALING, {
    skip: !agentId,
    variables: { agentId },
    onData: ({ data }) => {
      const payload = data?.data?.browserCallAgentSignaling;
      if (!payload) return;
      const { event, roomId, sdp, call, issueCode } = payload;

      if (event === "guest_issue") {
        // Customer hit a setup error (no mic, blocked permissions, etc.)
        // before they could connect. Pop a warning snackbar so the agent
        // can text them — the call row itself was already marked failed
        // and a chat card was inserted by the backend resolver.
        enqueueSnackbar(guestIssueSnackbarText(issueCode, call?.customerName), {
          variant: "warning",
          autoHideDuration: 8000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        // Remove the (now-failed) call from the incoming-toast queue if
        // it was still there. Safe even if it wasn't — filter is a no-op.
        if (roomId) {
          setIncomingQueue((q) => q.filter((c) => c.roomId !== roomId));
        }
        return;
      }

      if (event === "guest_joined" && sdp && call) {
        setIncomingQueue((q) => {
          if (q.some((c) => c.roomId === roomId)) return q;
          return [
            ...q,
            {
              roomId,
              customerName: call.customerName || null,
              customerIdentifier: call.customerIdentifier || null,
              channel: call.channel || "standalone",
              mediaMode: call.mediaMode || "audio",
              sdpOffer: sdp,
              receivedAt: Date.now(),
            },
          ];
        });
      } else if (event === "ended" || event === "agent_cancelled") {
        setIncomingQueue((q) => q.filter((c) => c.roomId !== roomId));
        if (activeRoomId === roomId) {
          setActiveRoomId(null);
        }
        if (roomId) {
          if (popoutHandlingEndRef.current.has(roomId)) {
            // Pop-out is alive and handling teardown. The watchdog was
            // already armed when POPOUT_ENDED arrived — give it time to
            // upload / retry. No-op here.
          } else if (recordingBuffersRef.current.has(roomId)) {
            // Pop-out is gone (refresh / crash / window close) but we
            // have its chunks mirrored. Upload right away — no need to
            // wait for a retry that can never happen.
            runFallbackUpload(roomId);
          }
        }
      }
    },
    onError: (err) => {
      // Subscription errors here would spam the agent on every reconnect;
      // log only via the snackbar in development.
      // eslint-disable-next-line no-console
      console.warn("Browser-call subscription error", err);
    },
  });

  const focusPopout = useCallback(() => {
    if (popoutRef.current && !popoutRef.current.closed) {
      popoutRef.current.focus();
    }
  }, []);

  const acceptCall = useCallback(
    (roomId: string) => {
      const incoming = incomingQueue.find((c) => c.roomId === roomId);
      if (!incoming) return;
      try {
        sessionStorage.setItem(
          SESSION_KEY,
          JSON.stringify({
            roomId: incoming.roomId,
            sdpOffer: incoming.sdpOffer,
            customerName: incoming.customerName,
            customerIdentifier: incoming.customerIdentifier,
            channel: incoming.channel,
            mediaMode: incoming.mediaMode,
            agentId,
          }),
        );
      } catch (e) {
        enqueueSnackbar("Browser blocked the call window. Allow pop-ups.", {
          variant: "error",
        });
        return;
      }

      const popout = window.open(
        `/browser-call-active?roomId=${encodeURIComponent(roomId)}`,
        `browserCall_${roomId}`,
        POPOUT_FEATURES,
      );
      if (!popout) {
        enqueueSnackbar(
          "Pop-up blocked. Allow pop-ups for this site to take the call.",
          { variant: "error" },
        );
        return;
      }
      popoutRef.current = popout;
      setActiveRoomId(roomId);
      setIncomingQueue((q) => q.filter((c) => c.roomId !== roomId));
    },
    [incomingQueue, agentId],
  );

  const rejectCall = useCallback(
    async (roomId: string) => {
      try {
        await rejectBrowserCall({
          variables: { roomId, reason: "agent_rejected" },
        });
      } catch (err: any) {
        enqueueSnackbar(err?.message || "Failed to reject call.", {
          variant: "error",
        });
      } finally {
        setIncomingQueue((q) => q.filter((c) => c.roomId !== roomId));
      }
    },
    [rejectBrowserCall],
  );

  const endActiveCall = useCallback(async () => {
    if (!activeRoomId) return;
    // Tell the pop-out to hang up cleanly; it will fire the mutation.
    channelRef.current?.postMessage({
      type: "END_FROM_MAIN",
      roomId: activeRoomId,
    });
    // Belt + suspenders: also end server-side from here.
    try {
      await endBrowserCall({
        variables: { roomId: activeRoomId, reason: "agent_hangup" },
      });
    } catch (e) {
      // ignore — pop-out may have ended it first
    }
    setActiveRoomId(null);
  }, [activeRoomId, endBrowserCall]);

  const value = useMemo<BrowserCallAgentContextValue>(
    () => ({
      incomingQueue,
      activeRoomId,
      acceptCall,
      rejectCall,
      endActiveCall,
      focusPopout,
    }),
    [
      incomingQueue,
      activeRoomId,
      acceptCall,
      rejectCall,
      endActiveCall,
      focusPopout,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useBrowserCallAgent(): BrowserCallAgentContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error(
      "useBrowserCallAgent must be used within BrowserCallAgentProvider",
    );
  }
  return ctx;
}

export const BROWSER_CALL_SESSION_KEY = SESSION_KEY;
export const BROWSER_CALL_CHANNEL_NAME = CHANNEL_NAME;
